// Survey screen (the `survey` step). Twin of the desktop SurveyDialog. Fetches
// the survey content (name + questions) over the session via GET
// /sessions/:id/survey, then renders the shared SurveyForm with a Submit that
// stays disabled until every question is answered.
//
// Submit payload: { survey_file: <name>, responses: [{question_id, response}] }.
// The question UI and the answer/response logic live in ./survey/SurveyForm so
// the inline inter-trial survey can reuse them; the helpers are re-exported here
// for existing importers.

import { useEffect, useRef, useState } from "react";
import type { WebRendererProps } from "./registry";
import type { SurveyQuestion } from "../api/types";
import { fetchSurvey, smartQuestionCheck } from "../session/sessionDriver";
import { SessionApiError } from "../api/client";
import { Loading, Button } from "../ui/primitives";
import { SurveyForm, type SurveyResponse } from "./survey/SurveyForm";

export { isAnswered, toResponseString, filterInput } from "./survey/SurveyForm";

export default function SurveyRenderer({ step, session, onDone }: WebRendererProps) {
  const settings = (step.settings ?? {}) as Record<string, unknown>;
  const pauseAfter =
    typeof settings.pause_after_seconds === "number" ? settings.pause_after_seconds : 0;

  const [questions, setQuestions] = useState<SurveyQuestion[] | null>(null);
  const [surveyName, setSurveyName] = useState("");
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const endedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    (async () => {
      try {
        const r = await fetchSurvey(session);
        if (!alive) return;
        setSurveyName(r.name || "");
        setQuestions(r.questions || []);
      } catch (err) {
        if (!alive) return;
        if (err instanceof SessionApiError) setFailed(true);
        else setFailed(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [session, retryKey]);

  function handleSubmit(responses: SurveyResponse[]) {
    if (endedRef.current) return;
    endedRef.current = true;
    const result = { survey_file: surveyName, responses };
    const submit = () => onDone({ kind: "submit", result });
    if (pauseAfter > 0) window.setTimeout(submit, pauseAfter * 1000);
    else submit();
  }

  if (failed) {
    return (
      <div className="jol-survey jol-survey--center">
        <p>The survey could not be loaded.</p>
        <Button variant="primary" onClick={() => setRetryKey((k) => k + 1)}>
          Try again
        </Button>
      </div>
    );
  }
  if (questions === null) return <Loading message="Loading the survey..." />;

  const runSmartCheck = (
    questionId: string,
    answer: string,
    provenance?: Record<string, unknown>,
  ) =>
    smartQuestionCheck(session, {
      turnId:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      questionId,
      answer,
      ...(provenance ? { provenance } : {}),
    });

  return (
    <div className="jol-survey">
      <SurveyForm
        questions={questions}
        submitLabel="OK"
        onSubmit={handleSubmit}
        onSmartCheck={runSmartCheck}
      />
    </div>
  );
}
